import { API_REQUEST_TIMEOUT_MS } from '~/composables/api';
import { authService } from '~/services/auth-service';
import { disableDemoMode, isDemoMode } from '~/services/demo/mode';
import type { AuthUser, PbAccessToken } from '~/types/auth/auth';
import { ApiError } from '~/utils/errors';
import { positiveInteger } from '~/utils/positive-integer';
import { tokenIn } from '~/utils/session-token';

/**
 * The member an access token names: its `sub`, which Passport mints as the member's person id, a
 * string of digits. Null for anything else: no token, one that is not a JWT, a payload that is not
 * JSON, or a `sub` that is not a person id written the way Passport writes one.
 */
function memberOf(accessToken: string | null): number | null {
    const parts = accessToken?.replace(/^Bearer /, '').split('.') ?? [];
    const [, payload] = parts;
    if (parts.length !== 3 || !payload) return null;

    let claims: unknown;
    try {
        claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return null;
    }
    const subject = typeof claims === 'object' && claims !== null && 'sub' in claims ? claims.sub : undefined;
    return positiveInteger(subject);
}

/** An access token in the form requests carry it. */
function asBearer(token: string): string {
    return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

/** An access token cookie's value, in the form requests carry it. */
function bearerIn(value: unknown): string | null {
    const held = tokenIn(value);
    return held && asBearer(held);
}

/**
 * Run a renewal of the session once every renewal this app's other tabs asked for first has run.
 * The API spends a refresh token as it redeems it, so of two tabs presenting the same one, the
 * second would be refused. A browser's locks are per origin: the other Privacy Bee apps renew the
 * same session on their own, and a refusal they cause is settled against the cookie jar. Outside a
 * browser there are no locks, and nothing else to wait for.
 */
async function afterEarlierRenewals(renew: () => Promise<boolean>): Promise<boolean> {
    const locks = typeof navigator === 'undefined' ? null : navigator.locks;

    return locks ? await locks.request('pb-token-refresh', { mode: 'exclusive' }, renew) : await renew();
}

export const useAuthStore = defineStore(
    'auth',
    () => {
        const config = useRuntimeConfig();

        // Environment-scoped auth cookie names (suffixed on dev so they
        // never collide with production's cookies at .privacybee.com).
        const { tokenCookieName, refreshCookieName } = useAuthCookieNames();

        // The session lives in cookies on the parent domain. A cookie of the same name on this host
        // (a write through a useCookie ref leaves one, docs/GOTCHAS.md) is listed beside the
        // session's, first once the session's is the newer, and every read of a cookie by name here
        // takes the first. So the host-only form goes before anything reads the jar; deleting it
        // leaves the session's cookies alone.
        if (import.meta.client) {
            deleteCookie(tokenCookieName);
            deleteCookie(refreshCookieName);
        }

        // Never saved to storage. It is the identity saved member state is checked against
        // (stores/memberPersistence.ts), so it is only ever the member this document's /me
        // confirmed (getMe, refreshUser), never one a previous session left behind. Once
        // confirmed, the member goes only as the session ends here (endSession) or is handed over
        // (handOver): sessionIsOurs() compares every session this document is offered against it.
        const user = ref(null as AuthUser | null);

        // A demo session is authenticated without ever holding a token, so every
        // token-shaped path below has to treat it as already signed in.
        const demoMode = computed(() => isDemoMode());

        // The session's cookies, as the browser announces changes to them, or, in a browser with no
        // Cookie Store API, as this app's tabs do (announceSession). They live on the parent
        // domain, where every tab and every Privacy Bee app reads and renews them, and this store
        // only listens to them: a write through a useCookie ref carries no domain, so it would put a
        // host-only copy of an auth cookie beside the shared one, which a sign-out on the auth host
        // cannot delete and which a read of document.cookie can find in place of the shared one.
        const tokenCookie = useCookie<string | null>(tokenCookieName, { default: () => null, readonly: true });
        const refreshTokenCookie = useCookie<string | null>(refreshCookieName, { default: () => null, readonly: true });

        // Whether this document has handed its member over (handOver): the jar came to hold a
        // session that is not its own. From then on it holds no session and takes none, until a
        // page load boots as whoever the jar holds.
        let handedOver = false;

        // The session as this document holds it: the access token its requests carry, and the
        // refresh token that renews it. Each starts as the jar holds it and follows every change
        // announced to it, as far as the session is this document's to send (sessionIsOurs). What
        // this document learns first, it holds at once: a renewal it made, or one another tab or app
        // made that it takes.
        const token = ref(bearerIn(tokenCookie.value));
        const refreshToken = ref(tokenIn(refreshTokenCookie.value));
        watch(tokenCookie, async (announced) => {
            // A demo holds no token: the session in the jar is a real member's.
            if (demoMode.value || handedOver) return;

            const inJar = bearerIn(announced);
            if (inJar === null) {
                token.value = null;
            } else if (inJar !== token.value) {
                await takeAccessToken(inJar);
            }
        });
        watch(refreshTokenCookie, (announced) => {
            if (!handedOver) refreshToken.value = tokenIn(announced);
        });

        const isRefreshing = ref(false);
        let refreshPromise: Promise<boolean> | null = null;
        // The session this document let go of (releaseMember), so a later renewal can tell "another
        // tab wrote a new one" apart from "the same cookie we just released is still sitting there":
        // the released one is renewed, not taken back (renewSession).
        let releasedToken: string | null = null;

        // Request queue for handling requests during token refresh
        interface QueuedRequest {
            resolve: (token: string | null) => void;
            reject: (error: Error) => void;
        }
        const requestQueue: QueuedRequest[] = [];

        const loggedIn = computed(() => demoMode.value || !!token.value || !!user.value);

        // A session this browser can resume, which is more than `loggedIn` sees: the access token
        // lapses after 12 hours, and the refresh token that outlives it mints a new one without the
        // member seeing the auth app. The boot reads the member (getMe) whenever there is one. On a
        // page that needs no session it renews nothing before it asks (getValidTokenForRequest), so
        // there it asks only with an access token, and a session the jar holds as a refresh token
        // alone is not resumed (staysSignedOut).
        const hasSession = computed(() => demoMode.value || !!token.value || !!refreshToken.value);

        /**
         * A cookie as the browser's jar holds it now, whoever wrote it: useCookie hears of another
         * tab's or app's write only once the browser announces it. Read with the whole value after
         * the name, which may itself contain `=`.
         */
        function readCookieDirectly(name: string): string | null {
            if (import.meta.server) return null;

            const prefix = `${name}=`;
            const cookie = document.cookie
                .split(';')
                .map((pair) => pair.trim())
                .find((pair) => pair.startsWith(prefix));
            return cookie ? decodeURIComponent(cookie.slice(prefix.length)) || null : null;
        }

        /** The access token in the jar, in the form requests carry it. */
        function jarToken(): string | null {
            return bearerIn(readCookieDirectly(tokenCookieName));
        }

        /** The refresh token in the jar. */
        function jarRefreshToken(): string | null {
            return tokenIn(readCookieDirectly(refreshCookieName));
        }

        /**
         * The member the browser's session belongs to now: the subject of the access token in the
         * shared cookie jar, which Passport mints as the member's person id. Read from the jar
         * itself rather than `token`, which learns of a sign-in in another tab or app only once the
         * browser announces the cookie change. Null when the jar holds no access token, or one
         * whose subject cannot be read.
         */
        function sessionMemberId(): number | null {
            return memberOf(jarToken());
        }

        /**
         * Whether a session of `member` is this document's to send: the session of the member it
         * confirmed, or anyone's while it has confirmed no one yet, and never once it has handed its
         * member over. A session whose member cannot be read (null) is nobody's.
         */
        function sessionIsOurs(member: number | null): boolean {
            if (handedOver || member === null) return false;

            const confirmed = user.value?.identity.id;
            return confirmed === undefined || confirmed === member;
        }

        /**
         * Whether this document stays signed out: a page that needs no session (a public route), not
         * signed in (`loggedIn`). Such pages read `loggedIn` as "a member is here", and only the boot
         * confirms who that is (plugins/hooks.ts), so a session taken after it would sign the page in
         * with no member loaded. The document takes none, from the jar or by renewing one, and the
         * session stays in the jar for the next page load to boot as.
         */
        function staysSignedOut(): boolean {
            return !loggedIn.value && isPublicRoute();
        }

        /**
         * Take an access token this document did not write, from the jar or from a renewal: send it
         * from now on when its session is this document's own (sessionIsOurs), or hand the member
         * over. A session the jar holds with no access token names no one, and is not taken. A
         * document that stays signed out (staysSignedOut) takes none, and holds no member to hand
         * over. Answers whether this document holds it.
         */
        async function takeAccessToken(accessToken: string | null): Promise<boolean> {
            if (staysSignedOut()) return false;

            if (accessToken === null || !sessionIsOurs(memberOf(accessToken))) {
                // The first hand-over sends the browser to sign in from a page that needs a session
                // (goToSignIn), with no page to return to: whoever holds the session starts on their
                // own start page, and the page this document is on is its member's, `?person=` and
                // search included.
                if (!handedOver) {
                    handOver();
                    await goToSignIn(null);
                }
                return false;
            }

            token.value = accessToken;
            // A live chat widget holds the bearer it bootstrapped with for the life of its socket,
            // so it must be told to re-bootstrap. No-op when chat was never opened.
            notifySupportChatIdentityChanged();
            return true;
        }

        // Helper to check if current route is public
        function isPublicRoute(): boolean {
            if (import.meta.server) return false;

            try {
                const router = useRouter();
                const route = router.currentRoute.value;
                return route.meta.public === true;
            } catch {
                // Fallback: check pathname against known public routes
                const publicPaths = ['/auth/login', '/auth/callback', '/auth/logout', '/brokers'];
                return publicPaths.some(path => window.location.pathname.startsWith(path));
            }
        }

        // Function to get a valid token for requests - handles refresh and queueing
        async function getValidTokenForRequest(): Promise<string | null> {
            // Demo requests never reach the network, so there is nothing to authorize.
            if (demoMode.value) {
                return null;
            }

            // A document that handed its member over sends no session: the one in the jar is not its own.
            if (handedOver) {
                return null;
            }

            if (token.value) {
                return token.value;
            }

            // The jar can hold a token this document has not been told of yet: the browser announces
            // another tab's or app's write a task later, and a request can go out first.
            const fromJar = jarToken();
            if (fromJar) {
                return (await takeAccessToken(fromJar)) ? fromJar : null;
            }

            // A page that needs no session sends its request without one rather than renew it first:
            // the request may need none, and a refusal renews the session of the member the page
            // confirmed.
            if (isPublicRoute()) {
                return null;
            }

            // If already refreshing, queue the request
            if (isRefreshing.value) {
                return new Promise((resolve, reject) => {
                    requestQueue.push({ resolve, reject });
                });
            }

            // No token, try to refresh
            const refreshed = await refreshAccessToken();
            return refreshed ? token.value : null;
        }

        /**
         * Renew the session. Resolves true once this document holds an access token to send; false
         * once it holds none, because the session ended or is not its own. Rejects when the API
         * could not be asked, which says nothing about the session.
         */
        async function refreshAccessToken(): Promise<boolean> {
            // A demo session has nothing to refresh and must never be bounced to login.
            if (demoMode.value) {
                return true;
            }

            // A document that handed its member over renews nothing: the session in the jar is not its own.
            if (handedOver) {
                return false;
            }

            // Nor does one that stays signed out (staysSignedOut). A request it sent without a session
            // was refused because its route needs one, and the route guard sends a page that needs one
            // to sign in, which comes back as a page load that boots as the member.
            if (staysSignedOut()) {
                return false;
            }

            // If already refreshing, return the existing promise
            if (refreshPromise) {
                return refreshPromise;
            }

            // The access token this document was sending: a renewal another tab makes while this
            // one waits its turn replaces it in the jar.
            const replaced = token.value;
            isRefreshing.value = true;
            refreshPromise = afterEarlierRenewals(() => renewSession(replaced)).finally(() => {
                isRefreshing.value = false;
                refreshPromise = null;
            });

            return refreshPromise;
        }

        async function renewSession(replaced: string | null): Promise<boolean> {
            // The document handed its member over while this renewal waited its turn: the session in
            // the jar is the newcomer's, and redeeming its refresh token would renew it from here.
            if (handedOver) {
                processRequestQueue(null);
                return false;
            }

            // Another tab renewed the session while this one waited its turn: the refresh token this
            // document would present is spent, and what that tab wrote answers this renewal.
            //
            // A token is only somebody else's renewal if it differs from BOTH the session being
            // replaced and the one this document released. The same cookie we let go of is not a
            // renewal — adopting it reports a member nobody confirmed — so that case falls through
            // to the refresh below, and the route guard confirms the member of the renewed session
            // before a page that needs one opens (initializeAuth).
            const renewedElsewhere = jarToken();
            if (renewedElsewhere && renewedElsewhere !== replaced && renewedElsewhere !== releasedToken) {
                return await takeSession(renewedElsewhere, jarRefreshToken());
            }

            const presented = jarRefreshToken();
            if (!presented) {
                await expireSession();
                return false;
            }

            try {
                // Sessions are minted under the API's confidential client, so only the API
                // can redeem their refresh tokens; Passport's /oauth/token rejects them from
                // this public client ("Token is not linked to client").
                const { data: tokenData } = await $fetch<{ data: PbAccessToken }>(
                    `${config.public.pb.authHost}/v3/auth/refresh`,
                    {
                        method: 'POST',
                        body: { refresh_token: presented },
                        timeout: API_REQUEST_TIMEOUT_MS,
                    }
                );

                // An empty jar says the session ended while this renewal was on its way: a sign-out in
                // another tab or app. The pair just minted renews a session nobody holds any more, and
                // writing it would sign the member back in, in every tab and app. It goes nowhere, and
                // the session ends here too.
                const inJar = jarRefreshToken();
                if (!inJar) {
                    await expireSession();
                    return false;
                }

                // Someone wrote a session into the jar while this renewal was on its way. It is newer
                // than the pair just minted, and writing that pair over it would sign it out.
                if (inJar !== presented) {
                    return await takeSession(jarToken(), inJar);
                }

                // The refresh token presented is spent, so the pair is the session's next, whoever's
                // it is, and it goes where every app reads the session. A refresh token says nothing
                // of whose session it renews, so the pair's access token says it, and this document
                // sends the pair only if it is its own.
                writeSession(tokenData);
                return await takeSession(`Bearer ${tokenData.access_token}`, tokenData.refresh_token);
            } catch (error) {
                // Only a 401 — the one status app-api's /v3/auth/refresh answers a refused, expired,
                // or reused token with — says the session is over. A refresh it never answered (no
                // connection, a timeout, a 5xx) says nothing, and neither does an edge 400/403 (a
                // Cloudflare challenge or a misroute: the refresh route itself never answers 400 or
                // 403). The cookies are shared by every Privacy Bee app: ending the session would
                // sign the member out of all of them, and clear what they saved, over a failure a
                // second try can recover from. So the session stays and the failure goes to every
                // request waiting on this refresh and to the caller, which the member can retry.
                const { code } = ApiError.fromFetchError(error);
                if (code !== 401) {
                    processRequestQueue(null);
                    throw error;
                }

                // A refusal says the token presented is spent, not whose session is left. The
                // session is over only while the jar still holds the token refused, or nothing. A
                // token another tab or app renewed with, or a session another member signed into,
                // lives on, and ending it would sign it out of every Privacy Bee app.
                const inJar = jarRefreshToken();
                if (!inJar || inJar === presented) {
                    await expireSession();
                    return false;
                }

                return await takeSession(jarToken(), inJar);
            }
        }

        /**
         * Take the session a renewal found, as the renewal read it: hold it when it is this
         * document's own (sessionIsOurs), or hand the member over. Every request waiting on the
         * renewal gets what this document holds after.
         */
        async function takeSession(accessToken: string | null, refresh: string | null): Promise<boolean> {
            // Whatever was released is answered by this: the document is holding a session again.
            releasedToken = null;

            if (!(await takeAccessToken(accessToken))) {
                processRequestQueue(null);
                return false;
            }

            refreshToken.value = refresh;
            processRequestQueue(token.value);
            return true;
        }

        // Process the request queue after refresh completes
        function processRequestQueue(newToken: string | null) {
            while (requestQueue.length > 0) {
                const queuedRequest = requestQueue.shift();
                if (queuedRequest) {
                    if (newToken) {
                        queuedRequest.resolve(newToken);
                    } else {
                        queuedRequest.reject(new Error('Token refresh failed'));
                    }
                }
            }
        }

        /** Delete the cookie `name` this host sees: the one written for `domain`, or the host-only one. */
        function deleteCookie(name: string, domain?: string) {
            const scope = domain ? `; domain=${domain}` : '';
            document.cookie = `${name}=; path=/${scope}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure`;
        }

        /**
         * Tell this app's other tabs that the session's cookies changed, once the jar holds the change.
         * A browser with the Cookie Store API announces every change itself, and there this posts
         * nothing. In one without it, useCookie refs hear of a change only on a BroadcastChannel, where
         * a ref posts only what it writes itself, and the refs here never write: refreshCookie posts that
         * the jar changed, and every tab's refs read it again. Without it, a sign-out leaves this app's
         * other tabs sending the session that ended.
         */
        function announceSession() {
            refreshCookie(tokenCookieName);
            refreshCookie(refreshCookieName);
        }

        // Helper function to clear auth cookies from browser
        function clearAuthCookies() {
            // The session is going; the widget and its transcript must go with it.
            resetSupportChat();
            // Only this environment's auth cookies: on dev the names are suffixed, so the unsuffixed
            // production cookies are never touched. Both forms go: the session's on the parent
            // domain, and a copy on this host.
            for (const name of [tokenCookieName, refreshCookieName, 'XSRF-TOKEN']) {
                deleteCookie(name, config.public.pb.domainHost);
                deleteCookie(name);
            }
            announceSession();
        }

        /**
         * This document lets go of the member it held, and leaves the browser's session to whoever
         * holds it: its cookies and the state saved in storage are not this document's to clear.
         * What goes is what this document held of the member: the identity saved state is checked
         * against (stores/memberPersistence.ts), the access token it sends, and the support widget,
         * which keeps the socket and transcript of the member it bootstrapped as.
         */
        function releaseMember() {
            resetSupportChat();
            releasedToken = token.value ?? jarToken();
            token.value = null;
            user.value = null;
        }

        /**
         * The jar holds a session that is not this document's to send (sessionIsOurs): another
         * member signed in on the auth host or in another app, or its member cannot be read. It is
         * left to whoever holds it, cookies and saved state included. This document lets go of its
         * member, and holds and takes no session again, so nothing it sends reaches that account.
         * Where the browser goes next is its caller's to say.
         */
        function handOver() {
            handedOver = true;
            releaseMember();
            refreshToken.value = null;
        }

        /**
         * End the session in this browser.
         *
         * Every way a session ends comes through here (signing out of this page's own session, and a
         * refresh that cannot mint a new token), so none of them leaves the next member on this
         * browser anything to inherit: not the identity, not the credentials, not the support chat,
         * not the state saved in storage.
         */
        function endSession() {
            // The identity goes first: a store saving after this finds no member to save for, so
            // nothing lands in storage once it is cleared.
            user.value = null;

            // A demo holds no credentials and saves no state of its own (stores/memberPersistence.ts).
            // The auth cookies and the saved state on this domain belong to a real session, and no
            // request is waiting on a refresh.
            if (demoMode.value) {
                disableDemoMode();
                return;
            }

            localStorage.clear();
            token.value = null;
            refreshToken.value = null;
            clearAuthCookies();
            processRequestQueue(null);
        }

        /**
         * Send the browser to sign in from a page that needs a session. The sign-in page sends
         * whoever signs in to `returnAddress` after (the `pb-redirect` cookie), or to their start
         * page when it is null.
         */
        async function goToSignIn(returnAddress: string | null) {
            // The internal login page keeps the redirect cookie on this domain.
            if (!isPublicRoute() && !window.location.pathname.startsWith('/auth/')) {
                useCookie<string | null>('pb-redirect').value = returnAddress;
                await navigateTo('/auth/login');
            }
        }

        /**
         * The session ran out and cannot be renewed. It ends, and the member signs in again and
         * comes back to the page they were on, query included.
         */
        async function expireSession() {
            endSession();
            await goToSignIn(window.location.pathname + window.location.search);
        }

        /**
         * Sign this document in for a page that needs a session, which shows the member /me
         * confirmed: hold the browser's session, renewing it when no access token is left, and
         * confirm its member. The route guard asks it whenever no member is confirmed (the boot's
         * /me went unanswered, or the boot found no session). Resolves true once a member is
         * confirmed; false once there is no session this document can send (none is left, it
         * ended, or it is another member's), and the guard sends the browser to sign in. Rejects
         * when the API could not be asked (the renewal or /me went unanswered), which says nothing
         * about the session: the navigation fails rather than open the page with no member, and
         * this document holds no access token, as after a boot whose /me went unanswered.
         */
        async function initializeAuth(): Promise<boolean> {
            if (!token.value && !(await refreshAccessToken())) {
                return false;
            }

            try {
                await refreshUser();
                return true;
            } catch (error) {
                // A /me the API refused set off a renewal, which let go of the session where it has
                // ended or is another member's.
                if (!loggedIn.value) return false;

                releaseMember();
                throw error;
            }
        }

        /**
         * Read the member (/me): again after a change to their account, such as an employment that
         * ended, or for the first time as the route guard signs a page in (initializeAuth). A
         * failed read rejects to the caller and changes nothing here. getMe is the boot's
         * confirmation of the member: it keeps a failure to itself, and lets go of the access token
         * of a document that has confirmed no one.
         */
        async function refreshUser(): Promise<void> {
            user.value = await authService.getMe();
        }

        async function getMe() {
            try {
                // /me goes out only with an access token: with none held, one is found first
                // (getValidTokenForRequest, which renews the session on a page that needs one), and
                // without one there is no member to read.
                if (!demoMode.value && !token.value && !(await getValidTokenForRequest())) {
                    throw new Error('No valid token available');
                }

                user.value = await authService.getMe();
            } catch {
                // /me did not confirm the member. A 401 is settled by the refresh it sets off, which
                // lets go of the member where the session has ended (endSession) or is another
                // member's (handOver). Anything else (no answer, a timeout, a 5xx) says nothing about
                // whose session the jar holds, so a document that confirmed its member keeps it, and
                // sessionIsOurs() goes on comparing every session it is offered against it. A
                // document that confirmed no one lets go of the access token it holds, so it is not
                // signed in (`loggedIn`) with no member loaded: a page that needs no session stays
                // that way (staysSignedOut), and the route guard confirms the member before a page
                // that needs one opens (initializeAuth).
                if (!user.value) releaseMember();
            }
        }

        /** Put a pair the API minted where every Privacy Bee app reads the session: on the parent domain. */
        function writeSession(tokenData: PbAccessToken) {
            const domain = config.public.pb.domainHost;
            const tokenExpires = new Date(Date.now() + (12 * 60 * 60 * 1000)); // 12 hours
            const refreshExpires = new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)); // 30 days

            document.cookie = `${tokenCookieName}=${encodeURIComponent(`Bearer ${tokenData.access_token}`)}; path=/; domain=${domain}; expires=${tokenExpires.toUTCString()}; secure; samesite=lax`;
            document.cookie = `${refreshCookieName}=${encodeURIComponent(tokenData.refresh_token)}; path=/; domain=${domain}; expires=${refreshExpires.toUTCString()}; secure; samesite=lax`;
            announceSession();
        }

        /**
         * Whether a sign-out may end the session in the jar: the page has not handed its member
         * over, and the jar's access token names no other member.
         *
         * The jar can hold another member's session while this page still shows its own member: the
         * browser announces a sign-in on the auth host or in another app a task later, and a browser
         * with no Cookie Store API never announces another app's write, so the page learns of it only
         * when a request or renewal of its own reads the jar. That session is not this page's to end:
         * clearing the jar signs its member out of every Privacy Bee app on this browser and clears
         * what they saved, and a sign-out ends every session of its member, on every device. A
         * session whose member cannot be read is not known to be anyone else's, and ends.
         */
        function mayEndSession(): boolean {
            const inJar = sessionMemberId();
            return !handedOver && (inJar === null || sessionIsOurs(inJar));
        }

        /**
         * Sign out: end the session this page holds at the API, on every device, then in this browser
         * and at the auth host. A session that is not the page's own is left to whoever holds it.
         */
        async function logout() {
            // A demo holds no session: it goes back to the auth app, and the real session in the jar
            // stays where it is.
            if (demoMode.value) {
                endSession();
                window.location.href = config.public.pb.authApp;
                return;
            }

            // Every sign-in session of the member ends at the API first: once the page has deleted the
            // cookie, the auth host's `/logout` may find none of them (docs/GOTCHAS.md, Sessions).
            //
            // The revoke takes the path every request takes, so a lapsed bearer is renewed and the
            // request sent again. A revoke that fails ends nothing at the API, and the member is still
            // signed out of this browser.
            //
            // Only the bearer this page holds goes out. A page that holds none (its access token
            // lapsed and went) sends no revoke: the jar is every app's, and a revoke presented as
            // the wrong member would sign that member out of every device.
            if (mayEndSession()) {
                if (token.value === null) {
                    console.warn('This page holds no bearer to end the sign-in sessions with; signing out of this browser alone');
                } else {
                    try {
                        await authService.revokeTokens();
                    } catch (error) {
                        console.warn('The API did not end the sign-in sessions; signing out of this browser alone:', error);
                    }
                }
            }

            // Asked again once the revoke is answered: another member can sign in while it is out, and
            // the renewal it needed can hand the member over. Then the page lets go of its member and
            // goes to the auth app, and the jar stays as its holder left it.
            if (!mayEndSession()) {
                handOver();
                window.location.href = config.public.pb.authApp;
                return;
            }

            endSession();
            window.location.href = `${config.public.pb.authHost}/logout?redirect=${config.public.pb.appDomain}`;
        }

        // Initiate login by redirecting to auth app
        async function login(redirectPath: string, email?: string | null, eraseme?: boolean) {
            const redirectUrl = `${window.location.origin}${redirectPath || '/dashboard'}`;

            let authUrl = `${config.public.pb.authApp}?redirect_url=${encodeURIComponent(redirectUrl)}`;
            if (email) {
                authUrl += `&email=${encodeURIComponent(email)}`;
            }
            // `redirect_url` encodes the original query string out of the auth
            // app's reach, so the EraseMe acquisition notice needs its own
            // top-level param to survive the hop.
            if (eraseme) {
                authUrl += '&eraseme=1';
            }

            window.location.href = authUrl;
        }

        return {
            user,
            token: readonly(token),
            loggedIn,
            hasSession,
            sessionMemberId,
            getMe,
            refreshUser,
            login,
            logout,
            refreshAccessToken,
            getValidTokenForRequest,
            initializeAuth,
            isRefreshing: readonly(isRefreshing),
        };
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
